/**
 * Compact circular icon buttons for "Sign in with Outlook" / "Sign in with Zoho
 * Mail" / "Sign in with LinkedIn". Unlike Google (which hands back an ID token
 * in-page via GIS), none of these providers ship a browser SDK — clicking one
 * does a full-page redirect through a real OAuth2 authorization-code + PKCE
 * flow, lands back on /oauth/callback/:provider, and that page forwards the
 * result to whichever login page started the flow (see OAuthCallback.jsx).
 */
import React from 'react';
import { generateCodeVerifier, generateCodeChallenge, randomState } from '@/utils/pkce';

const MICROSOFT_CLIENT_ID = process.env.REACT_APP_MICROSOFT_CLIENT_ID;
const ZOHO_CLIENT_ID = process.env.REACT_APP_ZOHO_CLIENT_ID;
const LINKEDIN_CLIENT_ID = process.env.REACT_APP_LINKEDIN_CLIENT_ID;

const PROVIDER_CONFIG = {
  microsoft: {
    clientId: MICROSOFT_CLIENT_ID,
    authEndpoint: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
    scope: 'openid email profile',
    extraParams: { response_mode: 'query' },
  },
  zoho: {
    clientId: ZOHO_CLIENT_ID,
    // FounderBoard's Zoho client lives on the India data center — Zoho accounts
    // are DC-sharded (.com/.in/.eu/...) and a client only works against its own DC.
    authEndpoint: 'https://accounts.zoho.in/oauth/v2/auth',
    scope: 'openid email profile',
    extraParams: { access_type: 'online', prompt: 'consent' },
  },
  linkedin: {
    clientId: LINKEDIN_CLIENT_ID,
    authEndpoint: 'https://www.linkedin.com/oauth/v2/authorization',
    scope: 'openid profile email',
    extraParams: {},
  },
};

export const OAUTH_PROVIDER_ENABLED = {
  microsoft: Boolean(MICROSOFT_CLIENT_ID),
  zoho: Boolean(ZOHO_CLIENT_ID),
  linkedin: Boolean(LINKEDIN_CLIENT_ID),
};

export async function startOAuthRedirect(provider, context, loginPath) {
  const cfg = PROVIDER_CONFIG[provider];
  if (!cfg?.clientId) return;
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = randomState();
  const redirectUri = `${window.location.origin}/oauth/callback/${provider}`;

  sessionStorage.setItem('oauth_pending', JSON.stringify({
    provider, context, codeVerifier, state, redirectUri, loginPath,
  }));

  const params = new URLSearchParams({
    client_id: cfg.clientId,
    response_type: 'code',
    redirect_uri: redirectUri,
    scope: cfg.scope,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    ...cfg.extraParams,
  });
  window.location.href = `${cfg.authEndpoint}?${params.toString()}`;
}

const MicrosoftIcon = () => (
  <svg viewBox="0 0 21 21" width="18" height="18" aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

const ZohoIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <rect x="1" y="4" width="22" height="16" rx="3" fill="#e42527" />
    <text x="12" y="16.5" textAnchor="middle" fontSize="12" fontWeight="700" fontFamily="Arial, Helvetica, sans-serif" fill="#fff">Z</text>
  </svg>
);

const LinkedinIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <rect x="1" y="1" width="22" height="22" rx="3" fill="#0A66C2" />
    <path fill="#fff" d="M7.12 9.4H4.13V19h2.99V9.4ZM5.62 8.14a1.73 1.73 0 1 0 0-3.46 1.73 1.73 0 0 0 0 3.46ZM19.87 19h-2.98v-5.05c0-1.2-.02-2.75-1.68-2.75-1.68 0-1.94 1.31-1.94 2.66V19H10.3V9.4h2.86v1.31h.04c.4-.75 1.37-1.54 2.82-1.54 3.02 0 3.85 1.99 3.85 4.57V19Z" />
  </svg>
);

const ICONS = { microsoft: MicrosoftIcon, zoho: ZohoIcon, linkedin: LinkedinIcon };
const LABELS = { microsoft: 'Continue with Outlook', zoho: 'Continue with Zoho Mail', linkedin: 'Continue with LinkedIn' };

const OAuthIconButton = ({ provider, context, loginPath, disabled = false }) => {
  if (!OAUTH_PROVIDER_ENABLED[provider]) return null;
  const Icon = ICONS[provider];
  return (
    <button
      type="button"
      onClick={() => startOAuthRedirect(provider, context, loginPath)}
      disabled={disabled}
      aria-label={LABELS[provider]}
      title={LABELS[provider]}
      data-testid={`oauth-${provider}-button`}
      className="w-10 h-10 rounded-full border border-border bg-surface flex items-center justify-center hover:bg-background transition-colors disabled:opacity-50 disabled:pointer-events-none"
    >
      <Icon />
    </button>
  );
};

export default OAuthIconButton;
